import { DEFAULT_ROOM_TYPE, resolveRoomTypeVocabId } from "./constants";
import type {
  InteriorDesignerState,
  InteriorDesignerAction,
  PanelSelections,
} from "./types";
import { TARGET_PANEL_TO_PANEL_ID } from "./types";
import { getActiveNodesSorted } from "./selectors";
import { getDescendantIds } from "../utils/getDescendantIds";

const IDLE_GENERATION: InteriorDesignerState["generation"] = {
  status: "idle",
  generationId: null,
  actions: null,
  progressMessage: null,
  elapsedSeconds: null,
  errorCode: null,
  parentPhotoId: null,
  lastFailedRequest: null,
};

/**
 * Spreads structured_prefill values onto the given panel selections,
 * only for keys that already exist on the target panel object.
 */
function applyStructuredPrefill({
  panelSelections,
  panelId,
  prefill,
}: {
  panelSelections: PanelSelections;
  panelId: keyof PanelSelections;
  prefill: Record<string, string>;
}): PanelSelections {
  const current = panelSelections[panelId];
  const updated = { ...current };
  for (const [key, value] of Object.entries(prefill)) {
    if (key in updated) {
      (updated as Record<string, string>)[key] = value;
    }
  }
  return {
    ...panelSelections,
    [panelId]: updated,
  };
}

export const createInitialState = ({
  photoId,
}: {
  photoId: string;
}): InteriorDesignerState => ({
  rootPhotoId: photoId,
  activeNodeId: photoId,
  tree: [],
  suggestions: [],
  activePhoto: null,
  isNavigating: false,
  navigatingFromNodeId: null,
  classification: {
    room_type: null,
    room_condition: null,
    furnished: null,
    inside_or_outside: null,
  },
  generation: { ...IDLE_GENERATION },
  activePanel: null,
  panelSelections: {
    interiorFinishing: {
      floor: null,
      wall: null,
      extra_wensen: "",
    },
    furnishing: {
      style: null,
      room_type: DEFAULT_ROOM_TYPE,
      extra_wensen: "",
    },
    wallColor: {
      ral_code: null,
      custom_text: "",
      extra_wensen: "",
    },
    otherWishes: {
      text: "",
    },
  },
  furnitureVisible: true,
  isOffline: false,
  hasCompletedFinishing: false,
  hasCompletedFurnishing: false,
});

export const interiorDesignerReducer = (
  state: InteriorDesignerState,
  action: InteriorDesignerAction,
): InteriorDesignerState => {
  switch (action.type) {
    case "PHOTO_LOADED": {
      const newestNode = getActiveNodesSorted({
        tree: action.tree,
        excludeId: state.rootPhotoId,
      })[0];
      const shouldNavigateToNewest = !!newestNode;
      const hasFinishingInTree = action.tree.some((node) =>
        node.actions?.some((a) => a.type === "interior_finishing"),
      );
      const hasFurnishingInTree = action.tree.some((node) =>
        node.actions?.some((a) => a.type === "furnishing"),
      );

      let panelSelections: PanelSelections = {
        ...state.panelSelections,
        furnishing: {
          ...state.panelSelections.furnishing,
          room_type:
            resolveRoomTypeVocabId({
              classificationRoomType: action.photo.classification.room_type,
            }) ?? DEFAULT_ROOM_TYPE,
        },
      };

      const appliedPanels = new Set<string>();
      for (const suggestion of action.suggestions) {
        const panelId = TARGET_PANEL_TO_PANEL_ID[suggestion.target_panel];
        if (!panelId || panelId === "otherWishes" || panelId === "history")
          continue;
        if (appliedPanels.has(panelId)) continue;
        appliedPanels.add(panelId);
        panelSelections = applyStructuredPrefill({
          panelSelections,
          panelId,
          prefill: suggestion.structured_prefill,
        });
      }

      return {
        ...state,
        tree: action.tree,
        suggestions: action.suggestions,
        ...(shouldNavigateToNewest
          ? {
              activeNodeId: newestNode.id,
              isNavigating: true,
              navigatingFromNodeId: state.activeNodeId,
            }
          : {
              activePhoto: {
                blob_url: action.photo.blob_url,
                classification: action.photo.classification,
              },
            }),
        classification: action.photo.classification,
        furnitureVisible: action.photo.classification.furnished === true,
        hasCompletedFinishing: hasFinishingInTree,
        hasCompletedFurnishing: hasFurnishingInTree,
        panelSelections,
      };
    }

    case "TREE_REFRESHED": {
      const hasFinishingInTree = action.tree.some((node) =>
        node.actions?.some((a) => a.type === "interior_finishing"),
      );
      const hasFurnishingInTree = action.tree.some((node) =>
        node.actions?.some((a) => a.type === "furnishing"),
      );
      return {
        ...state,
        tree: action.tree,
        hasCompletedFinishing: hasFinishingInTree,
        hasCompletedFurnishing: hasFurnishingInTree,
      };
    }

    case "PANEL_OPENED":
      return {
        ...state,
        activePanel: action.panel,
      };

    case "PANEL_CLOSED":
      return {
        ...state,
        activePanel: null,
      };

    case "SELECTION_CHANGED": {
      const { panel, field, value } = action;
      if (panel === "history") {
        return state;
      }
      const current =
        state.panelSelections[panel][
          field as keyof (typeof state.panelSelections)[typeof panel]
        ];
      if (current === value) return state;
      return {
        ...state,
        panelSelections: {
          ...state.panelSelections,
          [panel]: {
            ...state.panelSelections[panel],
            [field]: value,
          },
        },
      };
    }

    case "SUGGESTION_APPLIED": {
      const { panel, prefill, freeText } = action;
      if (panel === "history") {
        return state;
      }

      const withPrefill = applyStructuredPrefill({
        panelSelections: state.panelSelections,
        panelId: panel as keyof PanelSelections,
        prefill,
      });

      let otherWishesText =
        panel === "otherWishes"
          ? withPrefill.otherWishes.text
          : state.panelSelections.otherWishes.text;
      if (freeText !== null) {
        const trimmed = freeText.trim();
        if (trimmed) {
          const bullet = `• ${trimmed}`;
          if (otherWishesText && !otherWishesText.endsWith("\n")) {
            otherWishesText = `${otherWishesText}\n${bullet}`;
          } else {
            otherWishesText = `${otherWishesText}${bullet}`;
          }
        }
      }

      return {
        ...state,
        panelSelections: {
          ...withPrefill,
          otherWishes: {
            ...withPrefill.otherWishes,
            text: otherWishesText,
          },
        },
      };
    }

    case "GENERATION_REQUESTED":
      if (state.generation.status === "loading") return state;
      return {
        ...state,
        activePanel: null,
        generation: {
          ...state.generation,
          status: "loading",
          actions: action.actions,
          progressMessage: null,
          elapsedSeconds: null,
          generationId: null,
          errorCode: null,
          parentPhotoId: state.activeNodeId,
          lastFailedRequest: null,
        },
      };

    case "GENERATION_STARTED":
      if (state.generation.status !== "loading") return state;
      return {
        ...state,
        generation: {
          ...state.generation,
          generationId: action.generationId,
        },
      };

    case "GENERATION_PROGRESS":
      if (state.generation.status !== "loading") return state;
      if (
        state.generation.generationId !== null &&
        action.generationId !== state.generation.generationId
      ) {
        return state;
      }
      return {
        ...state,
        generation: {
          ...state.generation,
          progressMessage: action.message,
          elapsedSeconds: action.elapsedSeconds,
        },
      };

    case "GENERATION_SUCCEEDED": {
      if (state.generation.status !== "loading") return state;
      if (
        state.generation.generationId !== null &&
        action.generationId !== state.generation.generationId
      ) {
        return state;
      }
      const { photo } = action;
      const hasFinishing = photo.actions?.some(
        (a) => a.type === "interior_finishing",
      );
      const hasFurnishing = photo.actions?.some((a) => a.type === "furnishing");
      const successRoomType =
        resolveRoomTypeVocabId({
          classificationRoomType: photo.classification.room_type,
        }) ?? DEFAULT_ROOM_TYPE;
      return {
        ...state,
        tree: [
          ...state.tree.filter((node) => node.id !== photo.id),
          {
            id: photo.id,
            parent_photo_id: photo.parent_photo_id,
            origin: photo.origin,
            thumbnail_url: photo.thumbnail_url,
            actions: photo.actions,
            classification: photo.classification,
            created_at: photo.created_at,
            deleted_at: photo.deleted_at,
          },
        ],
        activeNodeId: photo.id,
        activePhoto: {
          blob_url: photo.blob_url,
          classification: photo.classification,
        },
        classification: photo.classification,
        isNavigating: false,
        navigatingFromNodeId: null,
        furnitureVisible: !!photo.classification.furnished,
        generation: { ...IDLE_GENERATION },
        hasCompletedFinishing: state.hasCompletedFinishing || !!hasFinishing,
        hasCompletedFurnishing: state.hasCompletedFurnishing || !!hasFurnishing,
        panelSelections: {
          ...state.panelSelections,
          furnishing: {
            ...state.panelSelections.furnishing,
            room_type: successRoomType,
          },
        },
      };
    }

    case "GENERATION_FAILED": {
      if (state.generation.status !== "loading") return state;
      if (
        state.generation.generationId !== null &&
        action.generationId !== state.generation.generationId
      ) {
        return state;
      }
      const revertFurniture = state.generation.actions?.some(
        (a) => a.type === "remove_furniture",
      );
      return {
        ...state,
        ...(revertFurniture && { furnitureVisible: true }),
        ...(action.errorCode === "vocab_version_mismatch" && {
          panelSelections: {
            ...state.panelSelections,
            interiorFinishing: {
              ...state.panelSelections.interiorFinishing,
              floor: null,
              wall: null,
            },
            furnishing: {
              ...state.panelSelections.furnishing,
              style: null,
              room_type:
                resolveRoomTypeVocabId({
                  classificationRoomType: state.classification.room_type,
                }) ?? DEFAULT_ROOM_TYPE,
            },
            wallColor: { ...state.panelSelections.wallColor, ral_code: null },
          },
        }),
        generation: {
          ...state.generation,
          status: "error",
          progressMessage: null,
          elapsedSeconds: null,
          errorCode: action.errorCode,
          lastFailedRequest: state.generation.actions
            ? {
                actions: state.generation.actions,
                parentPhotoId:
                  state.generation.parentPhotoId ?? state.activeNodeId,
              }
            : state.generation.lastFailedRequest,
        },
      };
    }

    case "GENERATION_RETRIED": {
      if (!state.generation.lastFailedRequest) {
        return state;
      }
      const needsNavigation =
        state.activeNodeId !== state.generation.lastFailedRequest.parentPhotoId;
      const hideFurniture = state.generation.lastFailedRequest.actions.some(
        (a) => a.type === "remove_furniture",
      );
      return {
        ...state,
        ...(hideFurniture && { furnitureVisible: false }),
        activeNodeId: state.generation.lastFailedRequest.parentPhotoId,
        activePhoto: needsNavigation ? null : state.activePhoto,
        isNavigating: needsNavigation,
        navigatingFromNodeId: needsNavigation ? state.activeNodeId : null,
        generation: {
          ...state.generation,
          status: "loading",
          actions: [...state.generation.lastFailedRequest.actions],
          parentPhotoId: state.generation.lastFailedRequest.parentPhotoId,
          progressMessage: null,
          elapsedSeconds: null,
          errorCode: null,
          generationId: null,
          lastFailedRequest: null,
        },
      };
    }

    case "GENERATION_DISMISSED": {
      const revertFurniture = state.generation.lastFailedRequest?.actions?.some(
        (a) => a.type === "remove_furniture",
      );
      return {
        ...state,
        ...(revertFurniture && { furnitureVisible: true }),
        generation: { ...IDLE_GENERATION },
      };
    }

    case "CONNECTIVITY_LOST":
      return {
        ...state,
        isOffline: true,
      };

    case "CONNECTIVITY_RESTORED":
      return {
        ...state,
        isOffline: false,
      };

    case "FURNITURE_TOGGLED":
      return {
        ...state,
        furnitureVisible: !state.furnitureVisible,
      };

    case "NODE_NAVIGATED":
      return {
        ...state,
        activeNodeId: action.nodeId,
        isNavigating: true,
        navigatingFromNodeId: state.navigatingFromNodeId ?? state.activeNodeId,
      };

    case "ACTIVE_PHOTO_FETCHED": {
      const resolvedRoomType =
        resolveRoomTypeVocabId({
          classificationRoomType: action.classification.room_type,
        }) ?? DEFAULT_ROOM_TYPE;
      return {
        ...state,
        activePhoto: {
          blob_url: action.blobUrl,
          classification: action.classification,
        },
        classification: action.classification,
        furnitureVisible: action.classification.furnished === true,
        isNavigating: false,
        navigatingFromNodeId: null,
        panelSelections: {
          ...state.panelSelections,
          furnishing: {
            ...state.panelSelections.furnishing,
            room_type: resolvedRoomType,
          },
        },
      };
    }

    case "NAVIGATION_FAILED":
      return {
        ...state,
        activeNodeId: state.navigatingFromNodeId ?? state.activeNodeId,
        isNavigating: false,
        navigatingFromNodeId: null,
      };

    case "NODE_SOFT_DELETED": {
      const deletedIds = getDescendantIds({
        nodeId: action.nodeId,
        tree: state.tree,
      });
      const updatedTree = state.tree.map((node) =>
        deletedIds.has(node.id)
          ? { ...node, deleted_at: action.deletedAt }
          : node,
      );
      const needsNavigation =
        deletedIds.has(state.activeNodeId) && action.fallsBackTo;
      const shouldClearNavFrom =
        state.navigatingFromNodeId !== null &&
        deletedIds.has(state.navigatingFromNodeId);
      return {
        ...state,
        tree: updatedTree,
        ...(shouldClearNavFrom ? { navigatingFromNodeId: null } : {}),
        ...(needsNavigation
          ? {
              activeNodeId: action.fallsBackTo as string,
              activePhoto: null,
              isNavigating: true,
              navigatingFromNodeId: null,
            }
          : {}),
      };
    }

    default:
      return state;
  }
};
